// ────────────────────────────────────────────────────────────────────────
// vehicleTypes.js — Catálogo de tipos de vehículo del módulo GPS.
//
// Cada tipo expone:
//   - id           : string canónico (matchea `device.tipo` del mock).
//   - label        : etiqueta en ESPAÑOL natural (se pasa por t() en consumidores).
//   - Lucide       : componente lucide-react para listas/inventario (vista lateral).
//   - materialIcon : nombre del Material Symbol para el marker del mapa.
//
// Persistencia del override por dispositivo en localStorage para que cada
// dispositivo pueda tener un ícono distinto (un camión vs un furgón vs un auto).
// El usuario lo cambia desde GpsDispositivos → columna "Tipo". El mapa, la lista
// y la ficha reaccionan en caliente al evento `gps-icon-change`.
//
// Cuando el back exista, reemplazar la persistencia local por PATCH del campo
// `tipo` (o un nuevo `iconType`) en el device. La forma del API queda igual.
// ────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';
import {
  Bike, Bus, Car, Caravan, PersonStanding, Plane, Ship, Truck,
} from 'lucide-react';

const STORAGE_KEY = 'gps.deviceIconOverrides';
const EVENT_NAME = 'gps-icon-change';

export const VEHICLE_TYPES = [
  { id: 'auto',         label: 'Auto',         Lucide: Car,    materialIcon: 'directions_car' },
  { id: 'furgon',       label: 'Furgón',       Lucide: Truck,  materialIcon: 'airport_shuttle' },
  { id: 'camion-3.5t',  label: 'Camión 3.5T',  Lucide: Truck,  materialIcon: 'local_shipping' },
  { id: 'camion-mayor', label: 'Camión mayor', Lucide: Truck,  materialIcon: 'fire_truck' },
  { id: 'taxi',         label: 'Taxi',         Lucide: Car,    materialIcon: 'local_taxi' },
  { id: 'bus',          label: 'Bus',          Lucide: Bus,    materialIcon: 'directions_bus' },
  { id: 'moto',         label: 'Motocicleta',  Lucide: Bike,   materialIcon: 'two_wheeler' },
  { id: 'bicicleta',    label: 'Bicicleta',    Lucide: Bike,   materialIcon: 'pedal_bike' },
  { id: 'caravana',     label: 'Caravana',     Lucide: Caravan,materialIcon: 'rv_hookup' },
  { id: 'avion',        label: 'Avión',        Lucide: Plane,  materialIcon: 'flight' },
  { id: 'barco',        label: 'Barco',        Lucide: Ship,   materialIcon: 'directions_boat' },
  { id: 'peaton',       label: 'Repartidor a pie', Lucide: PersonStanding, materialIcon: 'directions_walk' },
];

const TYPE_MAP = Object.fromEntries(VEHICLE_TYPES.map((t) => [t.id, t]));
const DEFAULT_TYPE = TYPE_MAP.auto;

function readOverrides() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Persiste el override de tipo para un dispositivo. Pasa `typeId=null` para
 * volver al tipo original del device. Notifica a la UI montada con el evento
 * `gps-icon-change` para re-renderizar mapa/lista en caliente.
 */
export function setVehicleType(deviceId, typeId) {
  if (!deviceId) return;
  const o = readOverrides();
  if (!typeId) delete o[deviceId];
  else o[deviceId] = typeId;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(o));
  } catch {}
  window.dispatchEvent(new Event(EVENT_NAME));
}

/** Resuelve el tipo: override del usuario > `device.tipo` > default. */
export function getVehicleType(device) {
  if (!device) return DEFAULT_TYPE;
  const overrides = readOverrides();
  const id = overrides[device.id] || device.tipo;
  return TYPE_MAP[id] || DEFAULT_TYPE;
}

/** Hook reactivo — re-renderiza cuando cambia el override del device. */
export function useVehicleType(device) {
  const [type, setType] = useState(() => getVehicleType(device));
  useEffect(() => {
    const update = () => setType(getVehicleType(device));
    update();
    window.addEventListener(EVENT_NAME, update);
    return () => window.removeEventListener(EVENT_NAME, update);
  }, [device]);
  return type;
}
